import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Course, Instructor } from '../types';
import CourseCard from '../components/CourseCard';
import PricingTable from '../components/PricingTable';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTracks } from '../hooks/useTracks';

const Home: React.FC = () => {
  usePageTitle();
  const tracks = useTracks();
  const { login, signUp, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Record<string, Instructor>>({});
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const coursesRes = await fetch('/api/courses');
        if (coursesRes.ok) {
          const courses: Course[] = await coursesRes.json();
          setAllCourses(courses.filter((c) => c.active));
        }

        const instructorsRes = await fetch('/api/instructors');
        if (instructorsRes.ok) {
          const instructorList: Instructor[] = await instructorsRes.json();
          const map: Record<string, Instructor> = {};
          instructorList.forEach((i) => (map[i.id] = i));
          setInstructors(map);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const displayedCourses = selectedTrack
    ? allCourses.filter((c) => c.track === selectedTrack)
    : allCourses;

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Patriot Academy Premium
          </h1>
          <p className="hero-subtitle">
            Expert-Led Microsoft Security Training
          </p>
          <p className="hero-description">
            Advance your cybersecurity career with hands-on, instructor-led courses aligned to
            Microsoft certification exams. Master identity, cloud security, and security operations
            with real-world labs and expert guidance.
          </p>
          {!isAuthenticated && (
            <div className="hero-cta">
              <button className="btn btn-primary btn-lg" onClick={login}>
                Existing Customer &mdash; Sign In
              </button>
              <button className="btn btn-secondary btn-lg" onClick={signUp}>
                New Customer &mdash; Sign Up
              </button>
            </div>
          )}
        </div>
        <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-number">{allCourses.length > 0 ? allCourses.length : '—'}</span>
            <span className="stat-label">Expert Courses</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{tracks.length}</span>
            <span className="stat-label">Learning Tracks</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">100%</span>
            <span className="stat-label">Hands-On Labs</span>
          </div>
        </div>
      </section>

      {/* Tracks Section */}
      <section className="section tracks-section">
        <div className="section-container">
          <h2 className="section-title">Training Tracks</h2>
          <p className="section-subtitle">
            Focused tracks to build your Microsoft security expertise.
            {' '}Click a track to filter courses below.
          </p>
          <div className="tracks-grid">
            {tracks.map((track) => (
              <div
                key={track.id || track.name}
                className={`track-card track-clickable ${selectedTrack === track.name ? 'track-selected' : ''}`}
                style={{ borderTopColor: track.color }}
                onClick={() => setSelectedTrack(selectedTrack === track.name ? null : track.name)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTrack(selectedTrack === track.name ? null : track.name); } }}
              >
                <div className="track-icon">{track.icon}</div>
                <h3>{track.name}</h3>
                <p>{track.description}</p>
              </div>
            ))}
          </div>
          {selectedTrack && (
            <div className="track-filter-indicator">
              Showing courses in: <strong>{selectedTrack}</strong>
              <button className="btn btn-text btn-sm" onClick={() => setSelectedTrack(null)}>Show All</button>
            </div>
          )}
        </div>
      </section>

      {/* Courses */}
      {displayedCourses.length > 0 && (
        <section className="section featured-section" id="courses">
          <div className="section-container">
            <h2 className="section-title">Courses</h2>
            <p className="section-subtitle">
              {selectedTrack
                ? `${displayedCourses.length} course${displayedCourses.length !== 1 ? 's' : ''} in this track`
                : 'Start your security training journey today'}
            </p>
            <div className="courses-grid">
              {displayedCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  instructor={instructors[course.instructorId]}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <section className="section pricing-section" id="pricing">
        <div className="section-container">
          <h2 className="section-title">Flexible Pricing</h2>
          <p className="section-subtitle">
            Choose the plan that fits your learning goals and team size
          </p>
          <PricingTable onSelectTier={(tier) => navigate(`/checkout?tier=${tier}`)} />
        </div>
      </section>

    </div>
  );
};

export default Home;
